const DB_NAME = 'whisper-web-cache';
const DB_VERSION = 2;
const TRANSCRIPTIONS_STORE = 'transcriptions';
const AUDIO_CHUNKS_STORE = 'audio-chunks';

export interface AudioChunk {
    id: string;
    timestamp: number;
    data: Uint8Array;
}

export interface CachedTranscription {
    id: string;
    timestamp: number;
    audioMetadata: {
        duration: number;
        sampleRate: number;
        numberOfChannels: number;
    };
    transcriptionData: {
        text: string;
        title?: string;
        chunks: { text: string; timestamp: [number, number | null] }[];
    };
}

class IndexedDBManager {
    private db: IDBDatabase | null = null;

    async init(): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.db = request.result;
                resolve();
            };

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                
                // Create or ensure transcriptions store exists
                if (!db.objectStoreNames.contains(TRANSCRIPTIONS_STORE)) {
                    db.createObjectStore(TRANSCRIPTIONS_STORE, { keyPath: 'id' });
                }
                
                // Create or ensure audio chunks store exists
                if (!db.objectStoreNames.contains(AUDIO_CHUNKS_STORE)) {
                    const store = db.createObjectStore(AUDIO_CHUNKS_STORE, { 
                        keyPath: ['id', 'timestamp']
                    });
                    store.createIndex('by_id', 'id');
                }
            };
        });
    }

    async saveTranscription(transcription: CachedTranscription): Promise<void> {
        if (!this.db) await this.init();
        
        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([TRANSCRIPTIONS_STORE], 'readwrite');
            const store = transaction.objectStore(TRANSCRIPTIONS_STORE);
            
            const request = store.put(transcription);
            
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
        });
    }

    async getTranscription(id: string): Promise<CachedTranscription | null> {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([TRANSCRIPTIONS_STORE], 'readonly');
            const store = transaction.objectStore(TRANSCRIPTIONS_STORE);
            
            const request = store.get(id);
            
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result || null);
        });
    }

    async getAllTranscriptions(): Promise<CachedTranscription[]> {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([TRANSCRIPTIONS_STORE], 'readonly');
            const store = transaction.objectStore(TRANSCRIPTIONS_STORE);
            
            const request = store.getAll();
            
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);
        });
    }

    async deleteTranscription(id: string): Promise<void> {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([TRANSCRIPTIONS_STORE], 'readwrite');
            const store = transaction.objectStore(TRANSCRIPTIONS_STORE);
            
            const request = store.delete(id);
            
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
        });
    }

    async clearOldTranscriptions(maxAge: number = 7 * 24 * 60 * 60 * 1000): Promise<void> {
        const cutoffTime = Date.now() - maxAge;
        const transcriptions = await this.getAllTranscriptions();
        
        const deletePromises = transcriptions
            .filter(t => t.timestamp < cutoffTime)
            .map(t => this.deleteTranscription(t.id));
        
        await Promise.all(deletePromises);
    }

    async saveAudioChunk(id: string, data: Uint8Array, timestamp: number): Promise<void> {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([AUDIO_CHUNKS_STORE], 'readwrite');
            const store = transaction.objectStore(AUDIO_CHUNKS_STORE);
            
            const request = store.put({ id, timestamp, data });
            
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
        });
    }

    async getAllChunks(id: string): Promise<AudioChunk[]> {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([AUDIO_CHUNKS_STORE], 'readonly');
            const store = transaction.objectStore(AUDIO_CHUNKS_STORE);
            const index = store.index('by_id');
            
            const request = index.getAll(id);
            
            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                const chunks = request.result;
                // Sort chunks by timestamp
                chunks.sort((a, b) => a.timestamp - b.timestamp);
                resolve(chunks);
            };
        });
    }

    async deleteAudioChunks(id: string): Promise<void> {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([AUDIO_CHUNKS_STORE], 'readwrite');
            const store = transaction.objectStore(AUDIO_CHUNKS_STORE);
            const index = store.index('by_id');
            
            // First get all keys for the given id
            const keyRequest = index.getAllKeys(id);
            
            keyRequest.onerror = () => reject(keyRequest.error);
            keyRequest.onsuccess = () => {
                const keys = keyRequest.result;
                const deletePromises = keys.map(key => 
                    new Promise<void>((resolve, reject) => {
                        const deleteRequest = store.delete(key);
                        deleteRequest.onerror = () => reject(deleteRequest.error);
                        deleteRequest.onsuccess = () => resolve();
                    })
                );
                
                Promise.all(deletePromises)
                    .then(() => resolve())
                    .catch(reject);
            };
        });
    }
}

export const dbManager = new IndexedDBManager();
